/**
 * SCSS Variables Export
 *
 * This file contains all the SCSS variables that were previously exported via :export.
 * These values are duplicated here for Turbopack compatibility, as Turbopack does not support SCSS :export.
 *
 * IMPORTANT: If you modify values in the SCSS files (dimensions.scss, screens.scss, colors.scss, breakpoints.scss),
 * you must also update the corresponding values in this file.
 */

// Colors (from colors.scss $brand map)
export const amethyst = '#c772fd'
export const black = '#000'
export const blackV2 = '#111'
export const coalMineCanary = '#fcd411'
export const coldSteel = '#afb4c2'
export const cyanora = '#3cd0f5'
export const darkGray = '#222'
export const futureFuscia = '#f66299'
export const offWhite = '#f6f7fa'
export const white = '#fff'

// Gradients
export const pinkBlackGradientVertical =
    'linear-gradient(2deg, #111 23.2%, #e64286 100.93%)'
export const pinkPurpleGradient =
    'linear-gradient(284deg, #7626c0 -25.56%, #be3d7f 100%)'
export const pinkPurpleGradientLighter =
    'linear-gradient(284deg, #3cd0f5 -25.56%, #cc61ff 42.48%, #f6629d 100%)'
export const pinkPurpleGradientVertical =
    'linear-gradient(45deg, #c84076 22.41%, #7726be 130.43%)'
export const purpleBlackGradientVertical =
    'linear-gradient(2deg, #111 23.2%, #c772fd 159.07%)'
export const turquoiseBlueGradient =
    'linear-gradient(85deg, #1b888f 2.14%, #26b7c1 80.05%)'

// Dimensions (from dimensions.scss)
export const announcementImageLargeHeight = '49px'
export const announcementImageSmallHeight = '41px'
export const controlWidthMedium = '250px'
export const controlWidthSmall = '180px'
export const fontSizeDefault = '18px'
export const gapLarge = '36px'
export const gapLarger = '48px'
export const gapMedium = '24px'
export const gapSmall = '16px'
export const gapXLarge = '72px'
export const gapXSmall = '8px'
export const gapXXLarge = '144px'
export const gapXXSmall = '5px'
export const iconSizeLarge = '60px'
export const iconSizeMedium = '40px'
export const iconSizeSmall = '20px'
export const iconSizeXSmall = '13px'
export const profileCardGifHeight = '200px'
export const sectionImageHeight = '300px'
export const sectionImageWidth = '320px'
export const sectionVideoHeight = '211px'
export const sectionVideoWidth = '375px'

// Avatar sizes (from dimensions.scss $avatar-sizes map)
export const avatarSizeLarge = '80px' // $icon-size-x-large
export const avatarSizeMedium = '40px' // $icon-size-medium
export const avatarSizeSmall = '20px' // $icon-size-small
export const avatarSizeXlarge = '130px' // $icon-size-xx-large

// Screen breakpoints (from screens.scss)
export const screenWidthLarge = '1000px'
export const screenWidthLargeTablet = '900px'
export const screenWidthMedium = '680px'
export const screenWidthSmall = '414px'
export const screenWidthXSmall = '320px'

// Breakpoints from breakpoints.scss (merged with screens.scss)
export const xxs = '320px'
export const xs = '480px'
export const sm = '667px'
export const md = '768px'
export const tablet = '900px'
export const largeTablet = '900px'
export const lg = '1024px'
export const xl = '1200px'

// Additional breakpoints from screens.scss
export const large = '1000px'
export const medium = '680px'
export const pressBannerLayoutShift = '500px'
export const small = '414px'
export const modalButtonLayoutShift = '375px'
export const xSmall = '320px'

// Default export as an object for dynamic access (e.g., variables[backgroundColor])
const variables = {
    // Colors
    amethyst,
    black,
    blackV2,
    coalMineCanary,
    coldSteel,
    cyanora,
    darkGray,
    futureFuscia,
    offWhite,
    white,

    // Gradients
    pinkBlackGradientVertical,
    pinkPurpleGradient,
    pinkPurpleGradientLighter,
    pinkPurpleGradientVertical,
    purpleBlackGradientVertical,
    turquoiseBlueGradient,

    // Dimensions
    announcementImageLargeHeight,
    announcementImageSmallHeight,
    controlWidthMedium,
    controlWidthSmall,
    fontSizeDefault,
    gapLarge,
    gapLarger,
    gapMedium,
    gapSmall,
    gapXLarge,
    gapXSmall,
    gapXXLarge,
    gapXXSmall,
    iconSizeLarge,
    iconSizeMedium,
    iconSizeSmall,
    iconSizeXSmall,
    profileCardGifHeight,
    sectionImageHeight,
    sectionImageWidth,
    sectionVideoHeight,
    sectionVideoWidth,

    // Avatar sizes
    avatarSizeLarge,
    avatarSizeMedium,
    avatarSizeSmall,
    avatarSizeXlarge,

    // Screen breakpoints
    screenWidthLarge,
    screenWidthLargeTablet,
    screenWidthMedium,
    screenWidthSmall,
    screenWidthXSmall,

    // Breakpoints
    xxs,
    xs,
    sm,
    md,
    tablet,
    largeTablet,
    lg,
    xl,

    // Additional breakpoints
    large,
    medium,
    pressBannerLayoutShift,
    small,
    modalButtonLayoutShift,
    xSmall,
}

export type Variable = keyof typeof variables

export default variables
